import { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../../Services/api'
import { useAuth } from '../../Hooks/Auth'
import { useToast } from '../../Hooks/ToasContext'
import { hubDashboardPalette } from '../../Styles/dashboard-palette'
import { HubDashboardApiRemoteService } from '../../Services/api/dashboard/hub-dashboard-service'
import { LoadHubDashboardUseCase } from '../../Usecase/Dashboard/load-hub-dashboard-usecase'
import { IChartDataItem } from './dashboard-charts.component'

import { IHubOrdersTimelineChartItem } from '../../Services/api/dashboard/hub-dashboard.interface'

export interface IHubDashboardStats {
  orders: number | null
  products: number | null
  customers: number | null
  collaborators: number | null
  stocks: number | null
  production: number | null
  loading: boolean
}

type HubDashboardStatKey = Exclude<keyof IHubDashboardStats, 'loading'>

const STAT_LABELS: Record<HubDashboardStatKey, string> = {
  orders: 'Pedidos',
  products: 'Produtos',
  customers: 'Clientes',
  collaborators: 'Colaboradores',
  stocks: 'Estoque',
  production: 'Produção',
}

const defaultStats: IHubDashboardStats = {
  orders: null,
  products: null,
  customers: null,
  collaborators: null,
  stocks: null,
  production: null,
  loading: true,
}

const ORDER_STATUS_GROUPS: Array<{ key: string; name: string; statuses: string[]; fill: string }> = [
  { key: 'open', name: 'Abertos', statuses: ['OPEN'], fill: hubDashboardPalette.orderStatus.open },
  { key: 'processing', name: 'Processando', statuses: ['PROCESSING', 'AWAITING_PICKING', 'IN_PICKING'], fill: hubDashboardPalette.orderStatus.processing },
  {
    key: 'billing',

    name: 'Faturamento',
    statuses: ['AWAITING_INVOICING', 'INVOICING', 'INVOICED', 'PARTIALLY_INVOICED', 'PROCESSING_INVOICE'],
    fill: hubDashboardPalette.orderStatus.billing,
  },
  { key: 'completed', name: 'Concluídos', statuses: ['COMPLETED'], fill: hubDashboardPalette.orderStatus.completed },
  { key: 'canceled', name: 'Cancelados', statuses: ['CANCELED', 'CANCELING'], fill: hubDashboardPalette.orderStatus.canceled },
]

export function useHubDashboard() {
  const { user } = useAuth()
  const { showToast } = useToast()
  const loadHubDashboardUseCase = useMemo(
    () => new LoadHubDashboardUseCase(new HubDashboardApiRemoteService(api)),
    [],
  )
  const [stats, setStats] = useState<IHubDashboardStats>(defaultStats)
  const [ordersChart, setOrdersChart] = useState<IChartDataItem[]>([])

  const [ordersTimeline, setOrdersTimeline] = useState<IHubOrdersTimelineChartItem[]>([])
  const [chartsLoading, setChartsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [failedModules, setFailedModules] = useState<string[]>([])

  const hasPermission = useCallback((permission: string) => {
    return (
      user?.permissionsScreens?.includes(`${permission}:manage`) ||
      user?.permissionsScreens?.includes('all') ||
      user?.permissionsScreens?.includes('all:hub')
    )
  }, [user?.permissionsScreens])

  const hasHcpAccess = useCallback(() => {
    return (
      user?.permissionsScreens?.includes('menu:hcp:manage') ||
      user?.permissionsScreens?.includes('all') ||
      user?.permissionsScreens?.includes('all:hcp')
    )
  }, [user?.permissionsScreens])

  const loadDashboard = useCallback(async () => {
    if (user?.resetPassword) {
      return
    }

    setStats((prev) => ({ ...prev, loading: true }))
    setChartsLoading(true)
    setLoadError(false)
    setFailedModules([])

    const requests: Array<{ key: HubDashboardStatKey; endpoint: string }> = []

    if (hasPermission('menu:orders')) {
      requests.push({ key: 'orders', endpoint: '/orders' })
    }
    if (hasPermission('menu:products')) {
      requests.push({ key: 'products', endpoint: '/products' })
    }
    if (hasPermission('menu:customers')) {
      requests.push({ key: 'customers', endpoint: '/customers' })
    }
    if (hasPermission('menu:collaborators')) {
      requests.push({ key: 'collaborators', endpoint: '/collaborators' })
    }
    if (hasPermission('menu:stocks')) {
      requests.push({ key: 'stocks', endpoint: '/stocks/products' })
    }
    if (hasPermission('menu:production')) {
      requests.push({ key: 'production', endpoint: '/service-orders' })
    }

    const canLoadOrdersDashboard = hasPermission('menu:orders')

    const orderChartPromise = canLoadOrdersDashboard
      ? loadHubDashboardUseCase.loadOrdersChart(ORDER_STATUS_GROUPS)
      : Promise.resolve(null)

    const orderTimelinePromise = canLoadOrdersDashboard

      ? loadHubDashboardUseCase.loadOrdersTimeline(30)

      : Promise.resolve(null)

    try {
      const [results, orderChartResult, orderTimelineResult] = await Promise.all([
        Promise.allSettled(
          requests.map((request) => loadHubDashboardUseCase.loadModuleTotal(request.endpoint)),
        ),
        orderChartPromise,

        orderTimelinePromise,
      ])

      const next: IHubDashboardStats = { ...defaultStats, loading: false }
      const failures: string[] = []

      results.forEach((result, index) => {
        const key = requests[index].key
        if (result.status === 'fulfilled' && !result.value.isFailure) {
          next[key] = result.value.getValue() ?? 0
        } else {
          next[key] = null
          failures.push(STAT_LABELS[key])
        }
      })

      if (orderChartResult && !orderChartResult.isFailure) {
        const chartPayload = orderChartResult.getValue()
        setOrdersChart(chartPayload?.data ?? [])
        if (chartPayload?.failed) {
          failures.push('Gráfico de pedidos')
        }
      } else if (orderChartResult?.isFailure) {
        setOrdersChart([])
        failures.push('Gráfico de pedidos')
      } else {
        setOrdersChart([])
      }

      if (orderTimelineResult && !orderTimelineResult.isFailure) {

        const timelinePayload = orderTimelineResult.getValue()

        setOrdersTimeline(timelinePayload?.data ?? [])

      } else if (orderTimelineResult?.isFailure) {

        setOrdersTimeline([])

        failures.push('Tendencia de pedidos')

      } else {

        setOrdersTimeline([])

      }



      setStats(next)
      setChartsLoading(false)

      if (failures.length > 0) {
        setLoadError(true)
        setFailedModules(failures)
        showToast(
          failures.length === 1
            ? `Não foi possível carregar: ${failures[0]}.`
            : 'Não foi possível carregar alguns indicadores do dashboard.',
          'error',
        )
      }
    } catch (error) {
      console.error('error loadHubDashboard:', error)
      setStats({ ...defaultStats, loading: false })
      setOrdersChart([])
      setOrdersTimeline([])
      setChartsLoading(false)
      setLoadError(true)
      setFailedModules(['Dashboard'])
      showToast('Não foi possível carregar o dashboard. Tente novamente.', 'error')
    }
  }, [hasPermission, loadHubDashboardUseCase, showToast, user?.resetPassword])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  const showOrdersChart = hasPermission('menu:orders')

  return {
    stats,
    ordersChart,
    ordersTimeline,
    chartsLoading: stats.loading || chartsLoading,
    showOrdersChart,
    hasPermission,
    hasHcpAccess,
    user,
    loadError,
    failedModules,
    refreshDashboard: loadDashboard,
  }
}
